import { type RefObject } from "react";
import OffersScreen, { type OffersScreenStyle } from "./OffersScreen";

/**
 * The Offers screen, optionally opened straight onto ONE offer (`POS-4`).
 *
 * ## Why this exists
 *
 * The home-screen banner is Sigge's demo flow #2: a guest who never went looking
 * presses "Bara 2 platser kvar!" and lands on that offer's hours. Landing them
 * on the rail instead - with the offer they just pressed somewhere among four
 * cards - would make the banner a signpost rather than a door.
 *
 * It is a HOST rather than a prop on {@link OffersScreen} because POS-7 is
 * rebuilding that screen's interior (the detail page, its focus grid, the hour
 * picker) at the same time as this ticket. Reaching inside it for a
 * `initialOfferId` would have been two units editing one focus model in one
 * wave. This composes the parts POS-7 already exports instead, and the seam is
 * deliberately trivial to remove: when the picker lands, `OffersEntry` forwards
 * `offerId` into `OffersScreen` and the detail branch below is deleted.
 *
 * ## Fail-soft, same rule as the banner
 *
 * An `offerId` that is not in the live feed - deleted between the banner
 * rendering and the press, sold out of its window, a stale id - falls through to
 * the rail. The guest sees the shop, never an error.
 */

interface Props {
  /** Open this offer's detail instead of the rail. Absent = the rail. */
  offerId?: string | null;
  style: OffersScreenStyle;
  /** Leaves the screen entirely (the host's Back). */
  onBack: () => void;
  scopeRef: RefObject<HTMLElement>;
  title?: string;
  pad?: { x: number; y: number };
  hints?: boolean;
}

export default function OffersEntry({
  offerId,
  style,
  onBack,
  scopeRef,
  title,
  pad,
  hints,
}: Props) {
  // POS-7's picker landed, so the interim read-only detail page this host used
  // to render is gone: `OffersScreen` owns the detail view AND the picker, and
  // takes the deep link directly. Keeping a second detail renderer here would
  // be the "two implementations that agree by luck" the doctrine warns about -
  // this one could not book, and the guest would meet a dead end reached from
  // the very banner that promised the booking.
  return (
    <OffersScreen
      style={style}
      onBack={onBack}
      scopeRef={scopeRef}
      title={title}
      pad={pad}
      hints={hints}
      initialOfferId={offerId ?? undefined}
    />
  );
}
